import { SelectTypes } from '@genial/components';
import type { ReportingEnumsReportingFilePeriodEnum } from '@genial/services/dist/models';
import { useEffect, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { FeatureToggle, useUnleashFlags } from '../../utils/useUnleashFlags';
import { sessionState } from '../session/atom';

import { useReportGeneratorActions } from './actions';
import {
  reportGeneratorAdditionalParametersState,
  reportGeneratorCatalogCategoryFilterState,
  reportGeneratorCategoryState,
  reportGeneratorRecentSameReportState,
  reportGeneratorReferenceDateState,
  reportGeneratorRequestAdvisorIdState,
  reportGeneratorRequestLoadingState,
  reportGeneratorRequestPartnerIdState,
} from './atom';
import {
  filterReportGeneratorCategoriesByFlags,
  findReportGeneratorDocumentType,
  findReportGeneratorSubCategory,
  getSubCategoriesByCategoryFilter,
  isFeeSettlementDocumentType,
  isProfitabilityDocumentType,
  REPORT_GENERATOR_CATEGORY_FILTER_ALL,
  ReportGeneratorCategory,
  ReportGeneratorDocumentType,
  ReportGeneratorParameterDefinition,
  ReportGeneratorSubCategory,
  toDocumentTypeSelectOptions,
} from './categories';
import { isReferenceDatePeriod } from './periods';
import { getPreviousMonthReferenceDate } from './referenceDateFromMonthYear';
import { reportGeneratorDocumentTypeSelector, reportGeneratorPeriodSelector } from './selector';
import { ReportGeneratorListItemNormalized } from './types';

export interface ReportGeneratorRequestState {
  selectedCategory?: ReportGeneratorCategory;
  selectedSubCategory?: ReportGeneratorSubCategory;
  categories: ReportGeneratorCategory[];
  catalogCategoryFilterId: string;
  visibleSubCategories: ReportGeneratorSubCategory[];
  showCategoryTabs: boolean;
  categoriesLoading: boolean;
  categoriesError?: Error;
  refreshCategories: () => void;
  documentType?: string;
  selectedDocumentType?: ReportGeneratorDocumentType;
  period?: ReportingEnumsReportingFilePeriodEnum;
  referenceDate?: Date;
  additionalParameters: Record<string, string>;
  parameterDefinitions: ReportGeneratorParameterDefinition[];
  loading: boolean;
  recentSameReport?: ReportGeneratorListItemNormalized;
  clearRecentSameReport: () => void;
  documentTypeOptions: SelectTypes.Option[];
  periodOptions: SelectTypes.Option[];
  setCatalogCategoryFilter: (categoryFilterId: string) => void;
  selectSubCategoryForRequest: (subCategory: ReportGeneratorSubCategory) => void;
  updateDocumentType: (value: string) => void;
  updatePeriod: (value: ReportingEnumsReportingFilePeriodEnum) => void;
  updateReferenceDate: (value: Date | undefined) => void;
  updateAdditionalParameter: (name: string, value: string) => void;
  requestPartnerId?: number;
  requestAdvisorId?: number;
  updateRequestPartnerId: (value: number) => void;
  updateRequestAdvisorId: (value: number | undefined) => void;
  requestExport: () => Promise<'success' | 'recent-same' | false>;
  getReportingFileAndDownload: (report: ReportGeneratorListItemNormalized) => Promise<void>;
  retryReportingFile: (report: ReportGeneratorListItemNormalized) => Promise<void>;
  canRequestExport: boolean;
}

const hasRequiredAdditionalParameters = (
  definitions: ReportGeneratorParameterDefinition[],
  values: Record<string, string>
): boolean =>
  definitions
    .filter((parameter) => parameter.required)
    .every((parameter) => Boolean(values[parameter.name]?.trim()));

export const useReportGeneratorRequest = (): ReportGeneratorRequestState => {
  const { b2bShowEquityReportGenerator, b2bShowProfitabilityReportGenerator, b2bEnableFixedFee } =
    useUnleashFlags(
      FeatureToggle.EquityReportGenerator,
      FeatureToggle.ProfitabilityReportGenerator,
      FeatureToggle.FixedFee
    );
  const { userProfile } = useRecoilValue(sessionState);
  const showEquity = Boolean(b2bShowEquityReportGenerator);
  const showProfitability = Boolean(b2bShowProfitabilityReportGenerator);
  const showFixedFee = Boolean(userProfile?.fixedFee && b2bEnableFixedFee);
  const showCategoryTabs = showEquity || showProfitability || showFixedFee;

  const documentType = useRecoilValue(reportGeneratorDocumentTypeSelector);
  const period = useRecoilValue(reportGeneratorPeriodSelector);
  const [categoryId, setCategoryId] = useRecoilState(reportGeneratorCategoryState);
  const [catalogCategoryFilterId, setCatalogCategoryFilterId] = useRecoilState(
    reportGeneratorCatalogCategoryFilterState
  );
  const [referenceDate, setReferenceDate] = useRecoilState(reportGeneratorReferenceDateState);
  const [additionalParameters, setAdditionalParameters] = useRecoilState(
    reportGeneratorAdditionalParametersState
  );
  const [requestPartnerId, setRequestPartnerId] = useRecoilState(
    reportGeneratorRequestPartnerIdState
  );
  const [requestAdvisorId, setRequestAdvisorId] = useRecoilState(
    reportGeneratorRequestAdvisorIdState
  );
  const [loading, setLoading] = useRecoilState(reportGeneratorRequestLoadingState);
  const [recentSameReport, setRecentSameReport] = useRecoilState(
    reportGeneratorRecentSameReportState
  );

  const {
    categories: categoriesData,
    categoriesLoading,
    categoriesError,
    refreshCategories,
    updateDocumentType,
    updatePeriod,
    updateReferenceDate,
    updateAdditionalParameter,
    postRequestNewReport,
    checkRecentSameRequest,
    getReportingFileAndDownload,
    retryReportingFile,
  } = useReportGeneratorActions();

  const categories = useMemo(
    () =>
      filterReportGeneratorCategoriesByFlags(
        categoriesData ?? [],
        showEquity,
        showProfitability,
        showFixedFee
      ),
    [categoriesData, showEquity, showProfitability, showFixedFee]
  );

  useEffect(() => {
    const isAllFilter = catalogCategoryFilterId === REPORT_GENERATOR_CATEGORY_FILTER_ALL;
    const isVisibleCategory = categories.some(
      (category) => category.id === catalogCategoryFilterId
    );

    if ((!showCategoryTabs || !isVisibleCategory) && !isAllFilter) {
      setCatalogCategoryFilterId(REPORT_GENERATOR_CATEGORY_FILTER_ALL);
    }
  }, [catalogCategoryFilterId, categories, setCatalogCategoryFilterId, showCategoryTabs]);

  const selectedCategory = useMemo(
    () => categories.find((category) => category.id === categoryId),
    [categories, categoryId]
  );

  const selectedDocumentType = useMemo(
    () => findReportGeneratorDocumentType(categories, documentType),
    [categories, documentType]
  );

  const selectedSubCategory = useMemo(
    () => findReportGeneratorSubCategory(categories, selectedDocumentType?.subCategoryId),
    [categories, selectedDocumentType?.subCategoryId]
  );

  const visibleSubCategories = useMemo(
    () => getSubCategoriesByCategoryFilter(categories, catalogCategoryFilterId),
    [categories, catalogCategoryFilterId]
  );

  const documentTypeOptions = useMemo(
    () => toDocumentTypeSelectOptions(selectedSubCategory?.documentTypes ?? []),
    [selectedSubCategory]
  );

  const periodOptions = selectedDocumentType?.periodOptions ?? [];

  const parameterDefinitions = selectedDocumentType?.parameters ?? [];

  const setCatalogCategoryFilter = (categoryFilterId: string) => {
    setCatalogCategoryFilterId(categoryFilterId || REPORT_GENERATOR_CATEGORY_FILTER_ALL);
  };

  const seedFeeSettlementScope = (nextDocumentTypeId: string) => {
    if (!isFeeSettlementDocumentType(Number(nextDocumentTypeId))) {
      setRequestPartnerId(undefined);
      setRequestAdvisorId(undefined);
      return;
    }

    setRequestPartnerId(userProfile?.idParceiro);
    setRequestAdvisorId(userProfile?.idAssessorIntranet);
  };

  const seedProfitabilityDefaults = (nextDocumentTypeId: string) => {
    if (!isProfitabilityDocumentType(Number(nextDocumentTypeId))) {
      return;
    }

    setReferenceDate(getPreviousMonthReferenceDate());
    const hasOptIn = (
      findReportGeneratorDocumentType(categories, nextDocumentTypeId)?.parameters ?? []
    ).some((parameter) => parameter.name.trim().toLowerCase() === 'optin');
    if (hasOptIn) {
      setAdditionalParameters({ optIn: '3' });
    }
  };

  const handleUpdateDocumentType = (value: string) => {
    updateDocumentType(value);
    seedFeeSettlementScope(value);
    seedProfitabilityDefaults(value);
  };

  const selectSubCategoryForRequest = (nextSubCategory: ReportGeneratorSubCategory) => {
    const firstDocumentType = nextSubCategory.documentTypes[0];
    if (!firstDocumentType) {
      return;
    }

    setCategoryId(nextSubCategory.categoryId);
    setReferenceDate(undefined);
    setAdditionalParameters({});
    handleUpdateDocumentType(firstDocumentType.id);
  };

  const isFeeSettlementRequest = isFeeSettlementDocumentType(Number(documentType));
  const hasFeeSettlementScope = (requestPartnerId ?? 0) > 0 && (requestAdvisorId ?? 0) > 0;
  const catalogRequiresPeriod = periodOptions.length > 0;
  const hasRequiredPeriod = !catalogRequiresPeriod || Boolean(period);
  const hasRequiredReferenceDate = !isReferenceDatePeriod(period) || Boolean(referenceDate);

  const requestExport = async (): Promise<'success' | 'recent-same' | false> => {
    if (!documentType || !hasRequiredPeriod || !hasRequiredReferenceDate) {
      return false;
    }

    if (isFeeSettlementRequest && !hasFeeSettlementScope) {
      return false;
    }

    if (!hasRequiredAdditionalParameters(parameterDefinitions, additionalParameters)) {
      return false;
    }

    setLoading(true);

    const payload = {
      documentType,
      periodSelected: period,
      referenceDate,
      additionalParameters,
    };

    const recentRequest = await checkRecentSameRequest(payload);
    if (recentRequest) {
      setLoading(false);
      setRecentSameReport(recentRequest);
      return 'recent-same';
    }

    await postRequestNewReport(payload);
    setLoading(false);
    return 'success';
  };

  const clearRecentSameReport = () => setRecentSameReport(undefined);

  const canRequestExport = Boolean(
    documentType &&
      hasRequiredPeriod &&
      hasRequiredReferenceDate &&
      (!isFeeSettlementRequest || hasFeeSettlementScope) &&
      hasRequiredAdditionalParameters(parameterDefinitions, additionalParameters)
  );

  return {
    selectedCategory,
    selectedSubCategory,
    categories,
    catalogCategoryFilterId,
    visibleSubCategories,
    showCategoryTabs,
    categoriesLoading,
    categoriesError,
    refreshCategories,
    documentType,
    selectedDocumentType,
    period,
    referenceDate,
    additionalParameters,
    parameterDefinitions,
    loading,
    recentSameReport,
    clearRecentSameReport,
    documentTypeOptions,
    periodOptions,
    setCatalogCategoryFilter,
    selectSubCategoryForRequest,
    updateDocumentType: handleUpdateDocumentType,
    updatePeriod,
    updateReferenceDate,
    updateAdditionalParameter,
    requestPartnerId,
    requestAdvisorId,
    updateRequestPartnerId: (value: number) => setRequestPartnerId(value),
    updateRequestAdvisorId: (value: number | undefined) => setRequestAdvisorId(value),
    requestExport,
    getReportingFileAndDownload,
    retryReportingFile,
    canRequestExport,
  };
};
